import React, { ChangeEvent, useEffect, useState } from "react";
import { FormattedNumber } from "react-intl";
import ReactModal from "react-modal";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IKitDetails from "../../../models/IKitDetails";
import IOrder from "../../../models/IOrder";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import IVendorModel from "../../../models/IVendorModel";
import "./KitOptionSelector.scss";
import OrderEntryService from "../../../services/OrderEntryService";
import OwButton from "../../shared/OwButton/OwButton";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../shared/OwLabel/OwLabel";
import UIService from "../../../services/UIService";

type Props = {
    kitCatalogItem: ICatalog_Item;
    order: IOrder;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    vendorModel: IVendorModel;
    updateItemSelections: (options: IKitDetails[]) => void;
    useOptionSelectionModal?: boolean;
    resetQty?: boolean;
    resetQtyCallbackFn?: () => void;
    closeOptions?: boolean;
    closeOptionsCallbackFn?: () => void;
};

export const KitOptionSelector: React.FC<Props> = (props) => {
    const initialOptionsQty: IKitDetails[] =
        props.kitCatalogItem.Kit_Items_Map?.map((kitItem) => {
            return { id: kitItem.id, qty: 0 };
        }) || [];

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [optionsVisible, setOptionsVisible] = useState(false);
    const [optionsQty, setOptionsQty] = useState(initialOptionsQty);
    const [optionsSelected, setOptionsSelected] = useState(0);

    useEffect(() => {
        if (props.resetQty) {
            let initialOptionsQty = optionsQty;

            initialOptionsQty = initialOptionsQty.map((option) => {
                option.qty = 0;

                return option;
            });

            setOptionsQty(initialOptionsQty);

            if (props.resetQtyCallbackFn) {
                props.resetQtyCallbackFn();
            }
        }
    }, [props.resetQty]);

    useEffect(() => {
        if (props.closeOptions) {
            setOptionsVisible(false);

            if (props.closeOptionsCallbackFn) {
                props.closeOptionsCallbackFn();
            }
        }
    }, [props.closeOptions]);

    useEffect(() => {
        let totalOptions = 0;

        for (const option of optionsQty) {
            if (Number(option.qty)) {
                totalOptions++;
            }
        }

        setOptionsSelected(totalOptions);

        props.updateItemSelections(optionsQty);
    }, [optionsQty]);

    const toggleOptionsView = () => {
        if (props.useOptionSelectionModal) {
            setModalIsOpen(true);
        } else {
            setOptionsVisible(!optionsVisible);
        }
    };

    const changeQty = (e: ChangeEvent<any>, index: number) => {
        if (Number(e.target.value) >= 0) {
            const newOptionsQty = [...optionsQty];

            newOptionsQty[index].qty = e.target.value;

            setOptionsQty(newOptionsQty);
        }
    };

    const getKitOptions = (baseOptionName?: string) => {
        return props.kitCatalogItem.Kit_Items_Map?.map((kitItem, index) => {
            const price = OrderEntryService.getKitPrice(props.kitCatalogItem, kitItem, props.supplierModel, props.order);

            if (!optionsQty[index]) {
                return null;
            }

            if (kitItem.hide) {
                return null;
            }

            return (
                <div className={"kit-option d-flex flex-column"} key={index}>
                    <div className={"d-flex "}>
                        <div className={"w-60"}>
                            <div className={"text-small"}>{kitItem.Catalog_Item?.name}</div>
                            {kitItem.is_kit_base_option ? (
                                <div className={"text-small"}>
                                    {baseOptionName ? <i>{baseOptionName}</i> : <OwLabel isItalic={true} title={"base_option"} />}
                                </div>
                            ) : null}
                            <div className={"price"}>
                                <b>
                                    <FormattedNumber value={price} style={"currency"} minimumFractionDigits={2} currency={"USD"} />
                                </b>
                            </div>
                        </div>
                        <div className={"w-40"}>
                            <OwInputWrapper
                                type={"number"}
                                name={"qty"}
                                fn={(e: ChangeEvent<any>) => {
                                    changeQty(e, index);
                                }}
                                value={optionsQty[index].qty}
                                onWheelFn={UIService.stopScrollWheel}
                            />
                        </div>
                    </div>
                    <div>
                        {props.kitCatalogItem.min_order_qty ? (
                            <div className={"text-small text-danger d-flex mt-2"}>
                                <OwLabel title={"min_order_qty"} /> {props.kitCatalogItem.min_order_qty}
                            </div>
                        ) : null}
                    </div>
                </div>
            );
        });
    };

    const closeModal = () => {
        setModalIsOpen(false);
    };

    return (
        <div className={"KitOptionSelector"}>
            <div
                className={
                    "kit-toggle-options d-flex justify-content-between align-items-center pointer " +
                    (optionsVisible ? "" : "bottom-border")
                }
                onClick={toggleOptionsView}>
                <span>
                    <OwLabel title={"options"} className={"pointer"} />
                    {optionsSelected > 0 ? <span className={"ms-2"}>(+{optionsSelected})</span> : ""}
                </span>
                {props.useOptionSelectionModal ? null : <i className={"fas " + (optionsVisible ? "fa-chevron-down" : "fa-chevron-up")} />}
            </div>
            <div className={"kit-options " + (optionsVisible ? "" : "d-none")}>
                {getKitOptions(props.kitCatalogItem.kit_base_option_name_override)}
            </div>
            {props.useOptionSelectionModal ? (
                <ReactModal isOpen={modalIsOpen} className={"modalClass"} overlayClassName={"overlayClass"}>
                    <div className={"modalKitOptions"}>{getKitOptions(props.kitCatalogItem.kit_base_option_name_override)}</div>
                    <div className={"d-flex mt-2 mb-2"}>
                        <div>
                            <OwButton title={"accept"} fn={closeModal} />
                        </div>
                    </div>
                </ReactModal>
            ) : null}
        </div>
    );
};
